// Production-ready frontend rate limiting utility
// Enforces max 1 submission per feedback token (individual user)
// Plus overall hospital rate limiting for abuse prevention
// Enhanced with IP tracking and stricter production limits

interface SubmissionRecord {
  timestamp: number;
  hospitalId: string;
  feedbackToken: string;
  userAgent?: string;
  ipFingerprint?: string;
}

interface RateLimitConfig {
  maxSubmissionsPerHospitalPerHour: number;
  maxSubmissionsPerIPPerHour: number;
  maxSubmissionsPerTokenLifetime: number;
  cleanupIntervalMs: number;
}

const RATE_LIMIT_KEY = 'questionnaire_submissions';
const IP_FINGERPRINT_KEY = 'user_fingerprint';

// Production-ready rate limits
const PRODUCTION_CONFIG: RateLimitConfig = {
  maxSubmissionsPerHospitalPerHour: 200, // Increased for mass feedback campaigns
  maxSubmissionsPerIPPerHour: 50, // Increased to accommodate mass distribution
  maxSubmissionsPerTokenLifetime: 1, // Strict: one submission per token ever
  cleanupIntervalMs: 60 * 60 * 1000 // 1 hour
};

/**
 * Generate a simple browser fingerprint for additional abuse prevention
 */
const generateBrowserFingerprint = (): string => {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Browser fingerprint', 2, 2);
    }
    
    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset(),
      canvas.toDataURL()
    ].join('|');
    
    // Simple hash function
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  } catch (error) {
    // Fallback to timestamp + random
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }
};

/**
 * Enhanced rate limit checking for production
 */
export const checkSubmissionRateLimit = (hospitalId: string, feedbackToken: string): {
  canSubmit: boolean;
  reason?: string;
  hospitalSubmissionsInLastHour?: number;
  ipSubmissionsInLastHour?: number;
  details?: string;
} => {
  if (!hospitalId || !feedbackToken) {
    return { 
      canSubmit: false, 
      reason: 'INVALID_PARAMETERS',
      details: 'Hospital ID and feedback token are required'
    };
  }

  try {
    // Get or generate browser fingerprint
    let ipFingerprint = localStorage.getItem(IP_FINGERPRINT_KEY);
    if (!ipFingerprint) {
      ipFingerprint = generateBrowserFingerprint();
      localStorage.setItem(IP_FINGERPRINT_KEY, ipFingerprint);
    }

    // Get existing submissions from localStorage
    const storedData = localStorage.getItem(RATE_LIMIT_KEY);
    const submissions: SubmissionRecord[] = storedData ? JSON.parse(storedData) : [];
    
    const now = Date.now();
    const oneHourAgo = now - PRODUCTION_CONFIG.cleanupIntervalMs;
    
    // Clean up old submissions (older than 1 hour)
    const recentSubmissions = submissions.filter(record => record.timestamp > oneHourAgo);
    localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(recentSubmissions));
    
    // 1. Check if this specific feedback token has already been used (STRICT)
    const tokenAlreadyUsed = submissions.some( // Check ALL submissions, not just recent
      record => record.feedbackToken === feedbackToken
    );
    
    if (tokenAlreadyUsed) {
      return { 
        canSubmit: false, 
        reason: 'TOKEN_ALREADY_USED',
        details: 'This feedback link has already been used'
      };
    }
    
    // 2. Check IP-based rate limiting (abuse prevention)
    const ipSubmissions = recentSubmissions.filter(
      record => record.ipFingerprint === ipFingerprint
    );
    
    if (ipSubmissions.length >= PRODUCTION_CONFIG.maxSubmissionsPerIPPerHour) {
      return { 
        canSubmit: false, 
        reason: 'IP_RATE_LIMIT_EXCEEDED',
        ipSubmissionsInLastHour: ipSubmissions.length,
        details: `Too many submissions from this device (${ipSubmissions.length}/${PRODUCTION_CONFIG.maxSubmissionsPerIPPerHour} per hour)`
      };
    }
    
    // 3. Check hospital-wide rate limiting (abuse prevention)
    const hospitalSubmissions = recentSubmissions.filter(
      record => record.hospitalId === hospitalId
    );
    
    if (hospitalSubmissions.length >= PRODUCTION_CONFIG.maxSubmissionsPerHospitalPerHour) {
      return { 
        canSubmit: false, 
        reason: 'HOSPITAL_RATE_LIMIT_EXCEEDED',
        hospitalSubmissionsInLastHour: hospitalSubmissions.length,
        details: `Hospital submission limit reached (${hospitalSubmissions.length}/${PRODUCTION_CONFIG.maxSubmissionsPerHospitalPerHour} per hour)`
      };
    }
    
    return {
      canSubmit: true,
      hospitalSubmissionsInLastHour: hospitalSubmissions.length,
      ipSubmissionsInLastHour: ipSubmissions.length
    };
    
  } catch (error) {
    console.error('Rate limit check failed:', error);
    // In production, be more conservative on errors
    return { 
      canSubmit: false, 
      reason: 'RATE_LIMIT_ERROR',
      details: 'Unable to verify submission limits. Please try again.'
    };
  }
};

/**
 * Record a successful submission with enhanced tracking
 */
export const recordSubmission = (hospitalId: string, feedbackToken: string): void => {
  if (!hospitalId || !feedbackToken) {
    console.error('Cannot record submission: missing required parameters');
    return;
  }

  try {
    // Get or generate browser fingerprint
    let ipFingerprint = localStorage.getItem(IP_FINGERPRINT_KEY);
    if (!ipFingerprint) {
      ipFingerprint = generateBrowserFingerprint();
      localStorage.setItem(IP_FINGERPRINT_KEY, ipFingerprint);
    }

    const storedData = localStorage.getItem(RATE_LIMIT_KEY);
    const submissions: SubmissionRecord[] = storedData ? JSON.parse(storedData) : [];
    
    // Add new submission record with enhanced tracking
    const newRecord: SubmissionRecord = {
      timestamp: Date.now(),
      hospitalId,
      feedbackToken,
      userAgent: navigator.userAgent.substring(0, 100), // Truncate for storage
      ipFingerprint
    };
    
    submissions.push(newRecord);
    
    // Keep only last 1000 records to prevent localStorage bloat
    const trimmedSubmissions = submissions.slice(-1000);
    localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(trimmedSubmissions));
    
  } catch (error) {
    console.error('Failed to record submission:', error);
    // Don't throw - this shouldn't block the submission
  }
};

/**
 * Get submission statistics for a hospital
 * @param hospitalId - The hospital ID to check
 * @returns Submission statistics
 */
export const getSubmissionStats = (hospitalId: string): {
  submissionsInLastHour: number;
  uniqueTokensUsed: number;
  oldestSubmissionTime?: Date;
} => {
  if (!hospitalId) {
    return { submissionsInLastHour: 0, uniqueTokensUsed: 0 };
  }
  
  try {
    const storedData = localStorage.getItem(RATE_LIMIT_KEY);
    const submissions: SubmissionRecord[] = storedData ? JSON.parse(storedData) : [];
    
    const now = Date.now();
    const oneHourAgo = now - PRODUCTION_CONFIG.cleanupIntervalMs;
    
    const recentSubmissions = submissions.filter(
      record => record.timestamp > oneHourAgo && record.hospitalId === hospitalId
    );
    
    const uniqueTokens = new Set(recentSubmissions.map(s => s.feedbackToken));
    
    let oldestSubmissionTime: Date | undefined;
    if (recentSubmissions.length > 0) {
      const oldestTimestamp = Math.min(...recentSubmissions.map(s => s.timestamp));
      oldestSubmissionTime = new Date(oldestTimestamp);
    }
    
    return {
      submissionsInLastHour: recentSubmissions.length,
      uniqueTokensUsed: uniqueTokens.size,
      oldestSubmissionTime
    };
    
  } catch (error) {
    console.error('Error getting submission stats:', error);
    return { submissionsInLastHour: 0, uniqueTokensUsed: 0 };
  }
};

/**
 * Check if a specific token has been used
 * @param feedbackToken - The feedback token to check
 * @returns boolean indicating if token was used
 */
export const isTokenAlreadyUsed = (feedbackToken: string): boolean => {
  if (!feedbackToken) return false;
  
  try {
    const storedData = localStorage.getItem(RATE_LIMIT_KEY);
    const submissions: SubmissionRecord[] = storedData ? JSON.parse(storedData) : [];
    
    const now = Date.now();
    const oneHourAgo = now - PRODUCTION_CONFIG.cleanupIntervalMs;
    
    const recentSubmissions = submissions.filter(record => record.timestamp > oneHourAgo);
    
    return recentSubmissions.some(record => record.feedbackToken === feedbackToken);
    
  } catch (error) {
    console.error('Error checking token usage:', error);
    return false;
  }
};

/**
 * Clear all submission records (for testing/admin purposes)
 */
export const clearSubmissionHistory = (): void => {
  try {
    localStorage.removeItem(RATE_LIMIT_KEY);
  } catch (error) {
    console.error('Error clearing submission history:', error);
  }
};

/**
 * Format time remaining until rate limit resets
 * @param milliseconds - Time in milliseconds
 * @returns Formatted string
 */
export const formatTimeUntilReset = (milliseconds: number): string => {
  const minutes = Math.ceil(milliseconds / (1000 * 60));
  
  if (minutes < 60) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (remainingMinutes === 0) {
    return `${hours} hour${hours !== 1 ? 's' : ''}`;
  }
  
  return `${hours}h ${remainingMinutes}m`;
};